import type { ManualVoiceAssignment } from "@/lib/castAnalysisTypes";
import { DEFAULT_VOICE_SETTINGS } from "@/lib/mockElevenVoices";

export function voicePreviewWebhookUrl(): string {
  const u = (import.meta.env.VITE_AISTORYCAST_PREVIEW_VOICE_WEBHOOK_URL as string | undefined)?.trim();
  return u && u.length > 0 ? u : "https://n8n.jdpenterprises.ai/webhook/aistorycast-preview-voice";
}

export type VoicePreviewOk = { success: true; audio_url: string };
export type VoicePreviewErr = { success: false; error: string; stage?: string };

export async function requestVoicePreview(args: {
  bookId: string;
  characterName: string;
  assignment: ManualVoiceAssignment;
  sampleText: string;
}): Promise<VoicePreviewOk | VoicePreviewErr> {
  const { bookId, characterName, assignment, sampleText } = args;
  const url = voicePreviewWebhookUrl();
  const voice_settings = {
    ...DEFAULT_VOICE_SETTINGS,
    ...(assignment.voice_settings ?? {}),
  };
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        book_id: bookId,
        character_name: characterName,
        voice_id: assignment.voice_id,
        voice_name: assignment.voice_name,
        sample_text: sampleText,
        voice_settings,
      }),
    });
    const raw = await res.text();
    let data: unknown = {};
    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      return { success: false, error: "Invalid JSON from preview service", stage: "parse" };
    }
    const o = data as Record<string, unknown>;
    if (!res.ok || o.success === false) {
      const err =
        typeof o.error === "string" && o.error.trim()
          ? o.error.trim()
          : typeof o.message === "string" && o.message.trim()
            ? o.message.trim()
            : `HTTP ${res.status}`;
      return {
        success: false,
        error: err,
        stage: typeof o.stage === "string" ? o.stage : undefined,
      };
    }
    const audio_url = typeof o.audio_url === "string" && o.audio_url.trim() ? o.audio_url.trim() : "";
    if (!audio_url) return { success: false, error: "Missing audio_url in response", stage: "response" };
    return { success: true, audio_url };
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : String(e), stage: "network" };
  }
}
